import { useState } from "react";
import { ChevronDown, ChevronRight, Copy } from "lucide-react";
import { toast } from "@/shared/components/toast";

interface DataPathTreeProps {
  /** Valor de muestra (JSON parseado) a explorar. */
  value: unknown;
  /** Convierte el path relativo ("body.tier") en el texto a copiar (p. ej. la expresión completa). */
  buildCopyText: (path: string) => string;
}

function previewOf(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value.length > 32 ? `"${value.slice(0, 32)}…"` : `"${value}"`;
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return "{…}";
  return String(value);
}

function isExpandable(value: unknown): value is Record<string, unknown> {
  // El evaluador de expresiones solo navega objetos (no índices de array):
  // los arrays y primitivos son hojas copiables tal cual.
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function TreeNode({
  name,
  value,
  path,
  depth,
  buildCopyText,
}: {
  name: string;
  value: unknown;
  path: string;
  depth: number;
  buildCopyText: (path: string) => string;
}) {
  const [open, setOpen] = useState(depth === 0);
  const expandable = isExpandable(value);

  const copy = async () => {
    const text = buildCopyText(path);
    await navigator.clipboard.writeText(text);
    toast.success(`Copiado: ${text}`);
  };

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 12 }}>
      <div className="flex items-center gap-1 group min-w-0">
        {expandable ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="p-0.5 rounded text-foreground/30 hover:text-foreground/60 shrink-0"
            aria-label={open ? "Contraer" : "Expandir"}
          >
            {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}
        <button
          type="button"
          onClick={copy}
          title={`Copiar ${buildCopyText(path)}`}
          className="flex items-center gap-1.5 min-w-0 text-left rounded px-1 py-0.5 hover:bg-accent/10 transition-colors"
        >
          <span className="text-[11px] font-mono font-semibold text-accent truncate">{name}</span>
          {!expandable && (
            <span className="text-[10px] font-mono text-foreground/35 truncate">{previewOf(value)}</span>
          )}
          <Copy className="size-2.5 text-foreground/25 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </button>
      </div>
      {expandable && open && (
        <div className="border-l border-black/5 ml-1.5">
          {Object.entries(value).map(([key, child]) => (
            <TreeNode
              key={key}
              name={key}
              value={child}
              path={path ? `${path}.${key}` : key}
              depth={depth + 1}
              buildCopyText={buildCopyText}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Árbol navegable de un JSON de muestra. Clic en cualquier campo copia la expresión
 * lista para pegar en las propiedades de un nodo o en una condición.
 */
export default function DataPathTree({ value, buildCopyText }: DataPathTreeProps) {
  if (!isExpandable(value)) {
    return (
      <p className="text-[11px] text-foreground/40 font-mono px-1">{previewOf(value)}</p>
    );
  }
  return (
    <div className="space-y-0.5">
      {Object.entries(value).map(([key, child]) => (
        <TreeNode
          key={key}
          name={key}
          value={child}
          path={key}
          depth={0}
          buildCopyText={buildCopyText}
        />
      ))}
    </div>
  );
}
